import { Grid, Typography } from '@material-ui/core';
import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OnChange } from 'react-final-form-listeners';
import QCXFinalFormDialog from '../../shared-components/dialog/QCXFinalFormDialog';
import QCXFinalPercentualField from '../../shared-components/final-percentual-field/QCXFinalPercentualField';
import QCXFinalAlternativeCurrencyField from '../../shared-components/final-currency-field/QCXFinalAlternativeCurrencyField';
import { CLOSED_STATUS, isOpenStatus } from '../../utils/hooks/form/dialog/formDialogUtils';
import { formatBrazilianNumericDecimal } from '../../utils/hooks/form/field/formatters';
import { normalizeNumeral, unnormalizeNumeral } from '../../utils/general/general-utils';
import QCXFinalSelectGanhoOuPerda, { GANHO, PERDA } from './QCXFinalSelectGanhoOuPerda';

export default function QCXApplyGanhoPerdaDialogForm({
  handleOnSubmit,
  handleFormStatus,
  formDialogStatus,
  initialValues,
  valorPagar = 0,
  valorBasePerda = 0,
  somenteGanho = false,
}) {
  const { t } = useTranslation();

  const isFormDialogOpen = useMemo(() => isOpenStatus(formDialogStatus), [formDialogStatus]);

  const handleCloseFormDialog = useCallback(() => {
    handleFormStatus(CLOSED_STATUS);
  }, [handleFormStatus]);

  const atualizaValores = useCallback(
    (form, values, campoAlterado) => () => {
      const isPerda = values?.tipo === PERDA;
      const base = isPerda ? valorBasePerda : valorPagar;

      let percentual = normalizeNumeral(_.get(values, 'percentual')) || 0;
      let valor = normalizeNumeral(_.get(values, 'valor')) || 0;

      if (campoAlterado === 'percentual') {
        valor = (base * percentual) / 100;
      } else if (base) {
        percentual = (valor * 100) / base;
      } else {
        percentual = 0;
      }

      if (valor > base) {
        valor = base;
        percentual = base ? 100 : 0;
      }

      form.change('percentual', unnormalizeNumeral(percentual, formatBrazilianNumericDecimal(5)));
      form.change('valor', unnormalizeNumeral(valor, formatBrazilianNumericDecimal(2)));
      form.change(
        isPerda ? 'valorFaturar' : 'valorPagamento',
        unnormalizeNumeral(base - valor, formatBrazilianNumericDecimal(2))
      );
    },
    [valorPagar, valorBasePerda]
  );

  const reiniciaValores = useCallback(
    (form) => (tipo) => {
      const base = tipo === PERDA ? valorBasePerda : valorPagar;

      form.change('percentual', undefined);
      form.change('valor', undefined);
      form.change(
        tipo === PERDA ? 'valorFaturar' : 'valorPagamento',
        unnormalizeNumeral(base, formatBrazilianNumericDecimal(2))
      );
    },
    [valorPagar, valorBasePerda]
  );


  return (
    <QCXFinalFormDialog
      key="form-dialog-ganho-perda-conta-pagar"
      id="form-dialog-ganho-perda-conta-pagar"
      title="Aplicar Ganho / Perda"
      dialogActionsProps={{
        button: {
          submit: {
            description: 'Aplicar',
            size: 'small',
            color: 'secondary',
          },
          cancel: {
            description: t('com.muralis.qcx.acoes.cancelar'),
            size: 'small',
          },
        },
      }}
      open={isFormDialogOpen}
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      onClose={handleCloseFormDialog}
    >
      {({ form, values }) => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <QCXFinalSelectGanhoOuPerda
              label={t('com.muralis.qcx.ganhoOuPerda')}
              name="tipo"
              required
              somenteGanho={somenteGanho}
            />
            <OnChange name="tipo">{reiniciaValores(form)}</OnChange>
          </Grid>

          {values?.tipo === PERDA && (
            <>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <Typography variant="body2" color="textSecondary">
                  A perda reduz apenas o valor a faturar do cliente. O valor a pagar ao fornecedor não muda.
                </Typography>
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12}>
                <QCXFinalPercentualField
                  id="perda-percentual-field"
                  key="perda-percentual-field"
                  name="percentual"
                  label={t('com.muralis.qcx.percentualPerda')}
                  scale={5}
                  onBlur={atualizaValores(form, values, 'percentual')}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12}>
                <QCXFinalAlternativeCurrencyField
                  id="perda-valor-field"
                  key="perda-valor-field"
                  name="valor"
                  label={t('com.muralis.qcx.valorPerda')}
                  onBlur={atualizaValores(form, values, 'valor')}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12}>
                <QCXFinalAlternativeCurrencyField
                  id="perda-valor-faturar-field"
                  key="perda-valor-faturar-field"
                  name="valorFaturar"
                  label={t('com.muralis.qcx.valorAFaturar')}
                  disabled
                />
              </Grid>
            </>
          )}

          {values?.tipo === GANHO && (
            <>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <QCXFinalPercentualField
                  id="ganho-percentual-field"
                  key="ganho-percentual-field"
                  name="percentual"
                  label={t('com.muralis.qcx.percentualGanho')}
                  scale={5}
                  onBlur={atualizaValores(form, values, 'percentual')}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12}>
                <QCXFinalAlternativeCurrencyField
                  id="ganho-valor-field"
                  key="ganho-valor-field"
                  name="valor"
                  label={t('com.muralis.qcx.valorGanho')}
                  onBlur={atualizaValores(form, values, 'valor')}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12}>
                <QCXFinalAlternativeCurrencyField
                  id="ganho-valor-pagamento-field"
                  key="ganho-valor-pagamento-field"
                  name="valorPagamento"
                  label={t('com.muralis.qcx.valorParaPagamento')}
                  disabled
                />
              </Grid>
            </>
          )}
        </Grid>
      )}
    </QCXFinalFormDialog>
  );
}
